import React, { useEffect, useState, useRef, useCallback } from "react";
import styles from "./Workshops.module.css";
import CustomButtons from "../../Components/CustomButtons/CustomButtons";
import cards from "../../Assets/cards.png";
import SpecialitiesCard from "../../Components/SpecialitiesCard/SpecialitiesCard";
import Offer from "../../Components/Offer/Offer";
import Recommended from "../../Components/Recommended/Recommended";
import ExpertService from "../../Components/ExpertService/ExpertService";
import ClientreviewCard from "../../Components/ClientreviewCard/ClientreviewCard";
import BlogsCard from "../../Components/BlogsCard/BlogsCard";
import AboutUs from "../../Components/AboutUs/AboutUs";
import ExpertDetailsWorkshop from "../../Components/ExpertDetailsWorkshop/ExpertDetailsWorkshop";
import BlogSlider from "../../Components/BlogSlider/BlogSlider";
import PricingCards from "../../Components/PricingCards/PricingCards";
import {
  open_api_with_cookies,
  open_api_without_auth,
  getCountryCode,
} from "../../Utils/network";
import ContactUsFrom from "../../Components/ContactUsFrom/ContactUsFrom";
import SliderBase from "../../Components/Slider/SliderBase";
import AppPromotion from "../../Components/Card/AppPromotion";
import { Helmet } from "react-helmet";
import SearchBar from "../../Components/Card/SearchBar";
import GeneralChips from "../../Components/Chips/GeneralChips";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import dummy from "../../Assets/dummy.png";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import WorkshopCard from "../../Components/WorkshopCard/WorkshopCard";
// Spinning loader component for infinite scroll
const LoadingSpinner = () => (
  <div className={styles.spinner_container} aria-label="Loading more workshops">
    <div className={styles.spinner}></div>
  </div>
);

// Skeleton for workshop cards
const WorkshopCardSkeleton = () => (
  <div className={styles.workshop_skeleton} aria-busy="true" aria-hidden="true">
    <Skeleton height={200} width="100%" style={{ marginBottom: "10px" }} />
    <Skeleton height={24} width="80%" style={{ marginBottom: "8px" }} />
    <Skeleton height={18} width="60%" style={{ marginBottom: "8px" }} />
    <Skeleton height={16} width="40%" />
  </div>
);

function Workshops() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [preReview, setPreReview] = useState([]);
  const [selectedChips, setSelectedChips] = useState(["all"]);
  const [chips, setChips] = useState([
    { id: 0, name: "all", display_name: "All" },
    { id: 1, name: "live", display_name: "Live Schedule" },
    { id: 2, name: "recorded", display_name: "Recorded" },
  ]);
  const [workshops, setWorkshops] = useState([]);
  const [filteredWorkshops, setFilteredWorkshops] = useState([]);
  const [visibleWorkshops, setVisibleWorkshops] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [maxPages, setMaxPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expertsData, setExpertsData] = useState({});
  const observer = useRef();
  const ITEMS_PER_PAGE = 12;

  const loadMoreWorkshops = useCallback(async () => {
    if (!hasMore || isLoadingMore || page >= maxPages) return;

    setIsLoadingMore(true);

    try {
      const nextPage = page + 1;

      // Build query params for the API call
      let type = selectedChips.includes("all") ? 0 : null;
      if (selectedChips.includes("live")) {
        type = 1;
      } else if (selectedChips.includes("recorded")) {
        type = 2;
      }

      const queryParams = new URLSearchParams();
      queryParams.append("page", nextPage);
      if (type !== null) queryParams.append("type", type);
      if (searchQuery) queryParams.append("search", searchQuery);

      const result = await open_api_with_cookies.get(
        `/home/workshop/filter/?${queryParams}`
      );

      const newWorkshops = result?.data?.data || [];
      const paginationInfo = {
        nextPage: result?.data?.next_page,
        maxPages: result?.data?.max_pages,
        totalCount: result?.data?.total_count,
      };

      // Append new workshops to existing ones
      setVisibleWorkshops((prev) => [...prev, ...newWorkshops]);
      setPage(nextPage);
      setHasMore(nextPage < paginationInfo.maxPages);

      console.log(
        `Loaded page ${nextPage}/${paginationInfo.maxPages}. Total workshops: ${visibleWorkshops.length + newWorkshops.length}/${paginationInfo.totalCount}`
      );
    } catch (err) {
      console.error("Error loading more workshops:", err);
      setError("Failed to load more workshops.");
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasMore,
    isLoadingMore,
    page,
    maxPages,
    selectedChips,
    searchQuery,
    visibleWorkshops.length,
  ]);

  const lastWorkshopElementRef = useCallback(
    (node) => {
      if (isLoadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreWorkshops();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoadingMore, hasMore, loadMoreWorkshops]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    getWorkshops();
    homeReviewCard();

    // Add event listener to reset scroll position when user navigates back
    const handlePopState = () => {
      window.scrollTo(0, 0);
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    filterWorkshops();
  }, [selectedChips, searchQuery, workshops]);

  useEffect(() => {
    // Fetch expert details for visible workshops
    const fetchExpertDetails = async () => {
      const workshopsWithExperts = visibleWorkshops.filter(
        (workshop) =>
          workshop.workshop_experts && workshop.workshop_experts.length > 0
      );

      if (workshopsWithExperts.length === 0) return;

      // Collect all expert IDs that aren't already fetched
      const expertIdsToFetch = new Set();
      workshopsWithExperts.forEach((workshop) => {
        workshop.workshop_experts.forEach((expert) => {
          // Extract the ID correctly based on data structure
          const expertId = typeof expert === "object" ? expert.id : expert;
          if (expertId && !expertsData[expertId]) {
            expertIdsToFetch.add(expertId);
          }
        });
      });

      if (expertIdsToFetch.size === 0) return;

      try {
        const expertPromises = Array.from(expertIdsToFetch).map((expertId) =>
          open_api_without_auth
            .get(`/profiles/expert/${expertId}`)
            .then((res) => ({ id: expertId, data: res.data }))
        );

        const results = await Promise.all(expertPromises);

        // Update experts data with new fetched data
        setExpertsData((prevData) => {
          const newData = { ...prevData };
          results.forEach((result) => {
            newData[result.id] = result.data;
          });
          return newData;
        });
      } catch (error) {
        console.error("Error fetching expert details:", error);
      }
    };

    fetchExpertDetails();
  }, [visibleWorkshops]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    // Reset pagination state when searching
    setPage(1);
    setHasMore(true);
    setVisibleWorkshops([]);

    // Reset chips to "all" when search is cleared
    if (!query) {
      setSelectedChips(["all"]);
    }
  };

  const getWorkshops = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await open_api_with_cookies.get(
        `/home/workshop/filter/?page=1&type=0`
      );
      const workshopsData = result?.data?.data || [];
      const paginationInfo = {
        maxPages: result?.data?.max_pages || 1,
        totalCount: result?.data?.total_count || 0,
        nextPage: result?.data?.next_page,
      };

      setWorkshops(workshopsData); // Store current page workshops
      setFilteredWorkshops(workshopsData);
      setVisibleWorkshops(workshopsData);
      setHasMore(paginationInfo.nextPage ? true : false);
      setPage(1);
      setMaxPages(paginationInfo.maxPages);
      setTotalCount(paginationInfo.totalCount);
    } catch (err) {
      setError("Failed to load workshops. Please try again later.");
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterWorkshops = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (selectedChips.includes("all") && !searchQuery) {
        // Reset to first page when showing all workshops
        const result = await open_api_with_cookies.get(
          `/home/workshop/filter/?page=1&type=0`
        );
        const workshopsData = result?.data?.data || [];
        const paginationInfo = {
          maxPages: result?.data?.max_pages || 1,
          totalCount: result?.data?.total_count || 0,
          nextPage: result?.data?.next_page,
        };

        setFilteredWorkshops(workshopsData);
        setVisibleWorkshops(workshopsData);
        setHasMore(paginationInfo.nextPage ? true : false);
        setPage(1);
        setMaxPages(paginationInfo.maxPages);
        setTotalCount(paginationInfo.totalCount);
        setIsLoading(false);
        return;
      }

      let type = selectedChips.includes("all") ? 0 : null;
      if (selectedChips.includes("live")) {
        type = 1; // LiveSchedule = 1
      } else if (selectedChips.includes("recorded")) {
        type = 2; // Recorded = 2
      }

      const queryParams = new URLSearchParams();
      queryParams.append("page", 1); // Always start from page 1 when filtering
      if (type !== null) queryParams.append("type", type);
      if (searchQuery) queryParams.append("search", searchQuery);

      const result = await open_api_with_cookies.get(
        `/home/workshop/filter/?${queryParams}`
      );
      const workshopsData = result?.data?.data || [];
      const paginationInfo = {
        maxPages: result?.data?.max_pages || 1,
        totalCount: result?.data?.total_count || 0,
        nextPage: result?.data?.next_page,
      };

      setFilteredWorkshops(workshopsData);
      setVisibleWorkshops(workshopsData);
      setHasMore(paginationInfo.nextPage ? true : false);
      setPage(1);
      setMaxPages(paginationInfo.maxPages);
      setTotalCount(paginationInfo.totalCount);
    } catch (err) {
      setError("Failed to filter workshops. Please try again.");
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const homeReviewCard = async () => {
    try {
      const res = await open_api_with_cookies.get(`home/testimonials`);
      setPreReview(res?.data?.results || []);
    } catch (err) {
      console.log(err);
    }
  };

  const handleClick = (event, chipName) => {
    setSelectedChips([chipName]);
    // Reset pagination state when changing filters
    setPage(1);
    setHasMore(true);
    setVisibleWorkshops([]);
  };

  return (
    <div>
      <Helmet>
        <title>MyBurgo - Parenting App | Pregnancy App | SelfCare App</title>
        <meta
          name="description"
          content="MyBurgo is India's comprehensive parenting app offering expert guidance, interactive workshops, personalized resources, and community support for parents and pregnant women. Empower your parenting journey with child development tools, curated recipes, mental health support, and more."
        />
      </Helmet>

      <div className={styles.container}>
        <div>
          <SliderBase page={"workshop"} />
        </div>
        <div className={styles.headers}>
          <h1 className={styles.title}>Discover wide range of workshops</h1>
        </div>
        <SearchBar onSearch={handleSearch} text="Search for any workshops" />

        <div
          className={styles.chip_container}
          role="group"
          aria-label="Filter workshops by type"
        >
          {chips.map((chip) => (
            <GeneralChips
              title={chip.display_name}
              key={chip.id}
              handleClick={(event) => handleClick(event, chip.name)}
              selectedChips={
                selectedChips.includes(chip.name) ? [chip.display_name] : []
              }
              aria-pressed={selectedChips.includes(chip.name)}
            />
          ))}
        </div>

        {error && (
          <p className={styles.error_message} role="alert">
            {error}
          </p>
        )}

        <div className={styles.workshops_container}>
          {isLoading ? (
            <div className={styles.workshops_grid} aria-live="polite">
              {Array(12)
                .fill(0)
                .map((_, index) => (
                  <div key={index}>
                    <WorkshopCardSkeleton />
                  </div>
                ))}
            </div>
          ) : visibleWorkshops.length > 0 ? (
            <>
              <div className={styles.workshops_grid}>
                {visibleWorkshops.map((workshop, index) => (
                  <div
                    key={workshop.id || index}
                    ref={
                      index === visibleWorkshops.length - 1
                        ? lastWorkshopElementRef
                        : null
                    }
                  >
                    <WorkshopCard data={workshop} is_workshop_page={true} />
                  </div>
                ))}
              </div>
              {isLoadingMore && <LoadingSpinner />}
              {!isLoadingMore &&
                hasMore &&
                totalCount > visibleWorkshops.length && (
                  <div className={styles.load_more_info}>
                    <p>
                      Showing {visibleWorkshops.length} of {totalCount}{" "}
                      workshops
                    </p>
                  </div>
                )}
            </>
          ) : (
            <div className={styles.no_results} role="status">
              <p>No workshops found matching your criteria.</p>
              <button
                className={styles.reset_button}
                onClick={() => {
                  setSelectedChips(["all"]);
                  setSearchQuery("");
                }}
              >
                Reset filters
              </button>
            </div>
          )}
        </div>
      </div>
      <section className={styles.ourclient}>
        <div className={styles.clients}>
          <p>OUR CLIENTS</p>
          <h1>Testimonials</h1>
        </div>
        <ClientreviewCard data={preReview} />
      </section>
      <AppPromotion />
      <div className={styles.bgColor}>
        {/* <section className={styles.blogs}>
          <h1 className={styles.blogName}>BLOGS </h1>
          <BlogSlider />
        </section> */}
        <section>
          <AboutUs />
        </section>
      </div>
      <ContactUsFrom />
    </div>
  );
}

export default Workshops;
